import type { ReactNode } from "react";
import { CardLayout } from "../components/CardLayout";
import { SectionTitle } from "../components/SectionTitle";
import type { HomeData } from "../TrialHomeScreen";
import { formatNumberForDisplay } from "@/utils/numberFormatting";
import {
  getClosedDeviationCount,
  getCrfMetrics,
  getMvrActionItemsMetrics,
  getOpenDeviationCount,
  getProtocolDeviationGroups,
  getSiteSdvMetrics,
} from "../utils";

export type CRFCardData = Pick<
  HomeData,
  "crf" | "crf_overdue" | "crf_required_sdv"
>;

export type SiteSDVCardData = Pick<
  HomeData,
  | "site_sdv"
  | "site_sdv_average"
  | "site_sdv_std"
  | "site_sdv_min"
  | "site_sdv_max"
>;

export type MVRActionItemsCardData = Pick<
  HomeData,
  | "mvr_action_items"
  | "mvr_action_items_open"
  | "mvr_action_items_open_60_days"
  | "mvr_action_items_closed"
>;

type ProtocolDeviationsCardData = Pick<HomeData, "protocol_deviations">;

export const DataSection = ({ data }: { data: HomeData }) => {
  return (
    <div>
      <SectionTitle title="DATA VALIDATION & COMPLIANCE" />
      <div className="grid lg:grid-cols-9 sm:grid-cols-2 grid-cols-1 gap-4">
        <div className="lg:col-span-3 col-span-1 h-full">
          <CRFCard data={data} />
        </div>
        <div className="lg:col-span-2 col-span-1 h-full">
          <SiteSDVCard data={data} />
        </div>
        <div className="lg:col-span-2 col-span-1 h-full">
          <MVRActionItemsCard data={data} />
        </div>
        <div className="lg:col-span-2 col-span-1 h-full">
          <ProtocolDeviationsCard data={data} />
        </div>
      </div>
    </div>
  );
};

export const CRFCard = ({ data }: { data: CRFCardData }) => {
  const { overdue, requiredSdv } = getCrfMetrics(data);

  return (
    <CardLayout
      title="CRF Items"
      subtitle="Count of Overdue CRFs & eCRFS requiring SDV"
      fullHeight={true}
    >
      <div className="p-4">
        <div className="grid grid-cols-2 divide-x divide-dashed divider-[#FFF]">
          <div className="flex flex-col items-center justify-center p-4">
            <p className="text-[40px] font-bold">{overdue}</p>
            <p className="text-[16px] text-center text-[#2B4752]">
              Overdue CRFs
            </p>
          </div>
          <div className="flex flex-col items-center justify-center p-4">
            <p className="text-[40px] font-bold">{requiredSdv}</p>
            <p className="text-[16px] text-center text-[#2B4752]">
              eCRFS requiring SDV
            </p>
          </div>
        </div>
      </div>
    </CardLayout>
  );
};

export const SiteSDVCard = ({ data }: { data: SiteSDVCardData }) => {
  const { average, standardDeviation, minimum, maximum } =
    getSiteSdvMetrics(data);

  return (
    <CardLayout
      title="Site SDV Complete"
      subtitle="CRF pages requiring SDV per site"
      fullHeight={true}
    >
      <div className="p-4">
        <table className="w-full text-[14px] text-[#2B4752]">
          <tbody>
            <tr className="w-full">
              <td className="text-start pb-4">Average</td>
              <td className="text-end font-bold pb-4">
                {formatNumberForDisplay(average)}
              </td>
            </tr>
            <tr>
              <td className="text-start pb-4">Standard Deviation</td>
              <td className="text-end font-bold pb-4">
                {formatNumberForDisplay(standardDeviation)}
              </td>
            </tr>
            <tr className="mt-4">
              <td className="text-start pb-4">Minimum</td>
              <td className="text-end font-bold pb-4">
                {formatNumberForDisplay(minimum)}
              </td>
            </tr>
            <tr className="mt-4">
              <td className="text-start">Maximum</td>
              <td className="text-end font-bold">
                {formatNumberForDisplay(maximum)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </CardLayout>
  );
};

const NumberCell = ({ children }: { children: ReactNode }) => {
  return <td className="text-end font-semibold text-[#2B4752]">{children}</td>;
};

const TextCell = ({ children }: { children: ReactNode }) => {
  return <td className="text-[#2B4752]">{children}</td>;
};

const ProtocolDeviationsCard = ({
  data,
}: {
  data: ProtocolDeviationsCardData;
}) => {
  const { important: importantDeviations, other: otherDeviations } =
    getProtocolDeviationGroups(data);

  return (
    <CardLayout
      title="Protocol Deviations"
      subtitle="Count of protocol deviations"
      fullHeight={true}
    >
      <div className="p-4 h-fit">
        <table className="w-full text-[14px] text-[#2B4752] h-full border-separate">
          <thead>
            <tr>
              <th className="capitalize text-start text-[#A9A9A9]">TYPE</th>
              <th className="text-end capitalize text-[#A9A9A9]">OPEN</th>
              <th className="text-end capitalize text-[#A9A9A9]">CLOSED</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <TextCell>Important (IPDs)</TextCell>
              <NumberCell>
                {getOpenDeviationCount(importantDeviations)}
              </NumberCell>
              <NumberCell>
                {getClosedDeviationCount(importantDeviations)}
              </NumberCell>
            </tr>
            <tr>
              <TextCell>Other PDs</TextCell>
              <NumberCell>{getOpenDeviationCount(otherDeviations)}</NumberCell>
              <NumberCell>
                {getClosedDeviationCount(otherDeviations)}
              </NumberCell>
            </tr>
          </tbody>
        </table>
      </div>
    </CardLayout>
  );
};

export const MVRActionItemsCard = ({
  data,
  longOpenLabel = "Open > 60 days",
}: {
  data: MVRActionItemsCardData;
  longOpenLabel?: string;
}) => {
  const { open, open60Days, closed } = getMvrActionItemsMetrics(data);

  return (
    <CardLayout
      title="MVR Action Items"
      subtitle="Count of MVR action items"
      fullHeight={true}
    >
      <div className="p-4">
        <table className="w-full text-[14px] text-[#2B4752] ">
          <thead className="sr-only">
            <tr>
              <th scope="col">Metric</th>
              <th scope="col">Value</th>
            </tr>
          </thead>
          <tbody>
            <tr className="w-full">
              <th scope="row" className="text-start pb-4 font-normal">
                Open
              </th>
              <td className="text-end font-bold pb-4">{open}</td>
            </tr>
            <tr>
              <th scope="row" className="text-start pb-4 font-normal">
                {longOpenLabel}
              </th>
              <td className="text-end font-bold pb-4">{open60Days}</td>
            </tr>
            <tr>
              <th scope="row" className="text-start pb-4 font-normal">
                Closed
              </th>
              <td className="text-end font-bold pb-4">{closed}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </CardLayout>
  );
};
